import sub from 'date-fns/sub';
import startOfDay from 'date-fns/startOfDay';
import type { Ari } from '@atlassian/jira-platform-ari/src/index.tsx';
import { JiraIssueAri } from '@atlassian/ari/jira/issue';
import type { preloadedDevSummaryDataQuery$data as preloadedDevSummaryDataQueryData } from '@atlassian/jira-relay/src/__generated__/preloadedDevSummaryDataQuery.graphql';
import { preloadedDevSummaryData } from './dev-summary-request/preloadedDevSummaryData.tsx';
import { fetchCreateBranchTargets } from './dev-summary-request/branch-target.tsx';
import type { RemoteAppResponse } from './dev-summary-request/types.tsx';
import { getHasDevelopmentDataInProject } from './dev-summary-request/has-development-in-project.tsx';

type FetchDevSummaryDataType = (
	issueAri: Ari,
) => Promise<preloadedDevSummaryDataQueryData | undefined>;

class PreloadedDataRequests {
	currentIssueAri?: Ari;

	fetchDevSummaryPromises: Map<Ari, Promise<preloadedDevSummaryDataQueryData | undefined>> =
		new Map();

	fetchCreateBranchTargetsPromises: Map<string, Promise<RemoteAppResponse>> = new Map();

	hasDevelopmentInProjectPromises: Map<string, Promise<boolean>> = new Map();

	clearAll() {
		this.fetchDevSummaryPromises.clear();
		this.fetchCreateBranchTargetsPromises.clear();
		this.hasDevelopmentInProjectPromises.clear();
	}

	getIssueAri() {
		return this.currentIssueAri;
	}

	setIssueAri(issueAri: Ari) {
		this.currentIssueAri = issueAri;
	}

	fetchAllAGGDependantRequests(issueAri: Ari) {
		this.getFetchDevSummaryPromise(issueAri);
		this.getFetchCreateBranchTargetsPromise(issueAri);
		// TODO: implement getHasDevelopmentInProject
	}

	getFetchDevSummaryPromise(issueAri: Ari) {
		let fetchDevSummaryPromise = this.fetchDevSummaryPromises.get(issueAri);

		if (fetchDevSummaryPromise) {
			return fetchDevSummaryPromise;
		}

		fetchDevSummaryPromise = preloadedDevSummaryData(issueAri);

		this.fetchDevSummaryPromises.set(issueAri, fetchDevSummaryPromise);

		return fetchDevSummaryPromise;
	}

	getFetchCreateBranchTargetsPromise(issueAri: Ari) {
		let fetchCreateBranchTargetsPromise = this.fetchCreateBranchTargetsPromises.get(issueAri);

		if (fetchCreateBranchTargetsPromise) {
			return fetchCreateBranchTargetsPromise;
		}

		const issue = JiraIssueAri.parse(issueAri);
		fetchCreateBranchTargetsPromise = fetchCreateBranchTargets(issue.issueId);

		this.fetchCreateBranchTargetsPromises.set(issueAri, fetchCreateBranchTargetsPromise);

		return fetchCreateBranchTargetsPromise;
	}

	getHasDevelopmentInProject(
		issueAri: Ari,
		projectAri: string,
		isJiraAdmin: boolean,
		isSiteAdmin: boolean,
	) {
		let hasDevelopmentInProjectPromise = this.hasDevelopmentInProjectPromises.get(issueAri);

		if (hasDevelopmentInProjectPromise) {
			return hasDevelopmentInProjectPromise;
		}

		const updatedFrom = this.getUpdatedFromDate();

		hasDevelopmentInProjectPromise = getHasDevelopmentDataInProject(
			projectAri,
			updatedFrom,
			isJiraAdmin,
			isSiteAdmin,
		);

		this.hasDevelopmentInProjectPromises.set(issueAri, hasDevelopmentInProjectPromise);

		return hasDevelopmentInProjectPromise;
	}

	private getUpdatedFromDate() {
		return startOfDay(
			sub(new Date(), {
				weeks: 6,
			}),
		).toISOString();
	}
}

/**
 * Singleton instance to manage preloaded data requests for issue view.
 */
export const preloadedDataRequests: PreloadedDataRequests = new PreloadedDataRequests();

export const fetchDevSummaryData: FetchDevSummaryDataType = (issueAri) => {
	return preloadedDataRequests.getFetchDevSummaryPromise(issueAri);
};
